import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Layers, RefreshCw, PlayCircle, XCircle, CheckCircle2, AlertTriangle,
  Clock, Search, ChevronDown, ChevronRight, Eye, EyeOff, Zap, Send, Bot,
  Beaker, ImagePlus, Image as Images, Copy, Flag,
} from 'lucide-react'
import {
  getCategories, getRestaurants, getPromptTemplates, getModels,
  generateCategory, getJobs, getJobItems, getJobEvents,
  resumeJob, cancelJob, pauseJob, reviewJob, getSetupStatus, getJobResults,
  retryFailedJob,
  testPrompt, searchItems, toggleFlag, getFlaggedResults,
} from '../api/client'
import { photoUrl } from '../lib/photos'
import { mediaGenFileUrl } from '../lib/media'
import { AuthedImage } from '../components/AuthedImage'
import { fmtLocal, fmtLocalShort } from '../lib/time'

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  queued: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  awaiting_review: 'bg-purple-100 text-purple-800',
  cancelling: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-gray-200 text-gray-700',
  pausing: 'bg-orange-100 text-orange-800',
  paused: 'bg-gray-200 text-gray-700',
  completed: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
}

const ACTIVE_STATUSES = new Set(['queued', 'running', 'cancelling', 'pausing'])

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function ReferenceImageUpload({ dataUrl, onChange }) {
  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      window.alert('Reference image must be 10MB or smaller (jpeg/png/webp).')
      return
    }
    onChange(await fileToDataUrl(file))
  }
  if (!dataUrl) {
    return (
      <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-600 hover:border-orange-400 hover:text-orange-600 cursor-pointer w-fit">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
        />
        <ImagePlus className="h-4 w-4" />
        Add reference image (optional — sent with every request)
      </label>
    )
  }
  return (
    <div className="flex items-center gap-3">
      <img src={dataUrl} alt="Reference" className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
      <div className="text-xs text-gray-600">
        Reference image attached — every request sends: product photo + this image + prompt
      </div>
      <button
        onClick={() => onChange(null)}
        className="ml-auto text-xs text-red-600 hover:underline"
      >
        Remove
      </button>
    </div>
  )
}

function StatusChip({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  )
}

function SetupBanner({ setup, onRefresh }) {
  if (!setup) return null
  const issues = []
  if (!setup.gcs.accessible) {
    issues.push(setup.gcs.set
      ? `GCS bucket "${setup.gcs.bucket}" is not accessible: ${setup.gcs.error || 'check IAM'}`
      : 'GCS_BUCKET is not set — create a bucket in europe-west4 and put its name in batch_food_pipeline/.env')
  }
  if (!setup.worker.ok) {
    issues.push(setup.worker.last_heartbeat_seconds_ago == null
      ? 'Batch worker has never started — run: python dashboard/scripts/worker.py'
      : `Batch worker heartbeat is ${setup.worker.last_heartbeat_seconds_ago}s old — is it still running?`)
  }
  if (issues.length === 0) {
    return (
      <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>Ready{setup.telegram ? ' — Telegram notifications enabled' : ''}.</span>
        <button onClick={onRefresh} className="ml-auto text-green-700 hover:text-green-900">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    )
  }
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
      <div className="flex items-center gap-2 text-red-800 font-medium mb-1">
        <AlertTriangle className="h-4 w-4" />
        Not ready for batch launches
        <button onClick={onRefresh} className="ml-auto text-red-700 hover:text-red-900">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <ul className="list-disc list-inside text-red-700 space-y-0.5">
        {issues.map((issue, i) => <li key={i}>{issue}</li>)}
      </ul>
    </div>
  )
}

function CategoryPicker({ categories, selected, onToggle, onSelectAll, onClear }) {
  const [search, setSearch] = useState('')
  const filtered = categories.filter(c => c.category.includes(search))
  return (
    <div className="border border-gray-300 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
      <div className="sticky top-0 bg-white p-2 flex items-center gap-2 border-b border-gray-200">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search categories..."
          className="flex-1 text-sm outline-none"
        />
        <button onClick={onSelectAll} className="text-xs text-orange-600 hover:underline">All</button>
        <button onClick={onClear} className="text-xs text-gray-500 hover:underline">None</button>
      </div>
      {filtered.map((c) => (
        <label key={c.category} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={selected.has(c.category)}
            onChange={() => onToggle(c.category)}
            className="rounded text-orange-500 focus:ring-orange-500"
          />
          <span className="flex-1 text-sm text-gray-800">{c.category}</span>
          <span className="text-xs text-gray-500">{c.photo_items} photos · {c.restaurants} restaurants</span>
        </label>
      ))}
      {filtered.length === 0 && (
        <div className="p-3 text-sm text-gray-500 text-center">No categories match</div>
      )}
    </div>
  )
}

function RestaurantPicker({ restaurants, selected, onToggle, onSelectAll, onClear }) {
  const [search, setSearch] = useState('')
  const filtered = restaurants.filter(r => r.name.includes(search))
  const allMode = selected.size === 0
  return (
    <div className="border border-gray-300 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
      <div className="sticky top-0 bg-white p-2 flex items-center gap-2 border-b border-gray-200">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search restaurants..."
          className="flex-1 text-sm outline-none"
        />
        <button onClick={onSelectAll} className="text-xs text-orange-600 hover:underline">All</button>
        <button onClick={onClear} className="text-xs text-gray-500 hover:underline">All (default)</button>
      </div>
      <div className="px-3 py-2 text-xs text-gray-500">
        {allMode ? 'All restaurants will be included. Tick specific ones to narrow down:' : `${selected.size} restaurant(s) selected`}
      </div>
      {filtered.map((r) => (
        <label key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={selected.has(r.name)}
            onChange={() => onToggle(r.name)}
            className="rounded text-orange-500 focus:ring-orange-500"
          />
          <span className="flex-1 text-sm text-gray-800">{r.name}</span>
          <span className="text-xs text-gray-500">{r.photo_count || 0} photos</span>
        </label>
      ))}
    </div>
  )
}

function NewBatchTab({ setup, onLaunched }) {
  const [categories, setCategories] = useState([])
  const [restaurants, setRestaurants] = useState([])
  const [templates, setTemplates] = useState([])
  const [models, setModels] = useState([])
  const [selectedCats, setSelectedCats] = useState(new Set())
  const [selectedRests, setSelectedRests] = useState(new Set())
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState('gemini-2.5-flash-image')
  const [chunkSize, setChunkSize] = useState(200)
  const [maxInFlight, setMaxInFlight] = useState(2)
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [qaGate, setQaGate] = useState(true)
  const [includeItemDetails, setIncludeItemDetails] = useState(false)
  const [skipGenerated, setSkipGenerated] = useState(true)
  const [refImage, setRefImage] = useState(null)
  const [mode, setMode] = useState('realtime')
  const [maxThreads, setMaxThreads] = useState(10)
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [launching, setLaunching] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    getCategories({ with_photos: true }).then(r => setCategories(r.data)).catch(() => {})
    getRestaurants().then(r => setRestaurants(r.data)).catch(() => {})
    getPromptTemplates().then(r => {
      setTemplates(r.data)
      const def = r.data.find(t => t.is_default) || r.data[0]
      if (def) setPrompt(prev => prev || def.prompt)
    }).catch(() => {})
    getModels().then(r => setModels(r.data)).catch(() => {})
  }, [])

  const toggle = (setter) => (value) => setter(prev => {
    const next = new Set(prev)
    next.has(value) ? next.delete(value) : next.add(value)
    return next
  })

  const requestBody = (dryRun) => ({
    prompt: prompt.trim(),
    model,
    categories: selectedCats.size ? [...selectedCats] : null,
    restaurants: selectedRests.size ? [...selectedRests] : null,
    chunk_size: Number(chunkSize) || 200,
    aspect_ratio: aspectRatio,
    qa_gate: qaGate,
    max_in_flight: Number(maxInFlight) || 2,
    include_item_details: includeItemDetails,
    reference_image: refImage || null,
    skip_generated: skipGenerated,
    dry_run: dryRun,
    mode,
    max_threads: Number(maxThreads) || 10,
  })

  const doPreview = async () => {
    setPreviewing(true); setError(null); setPreview(null)
    try {
      const res = await generateCategory(requestBody(true))
      setPreview(res.data)
    } catch (err) {
      setError(err.response?.data?.detail || err.message)
    } finally {
      setPreviewing(false)
    }
  }

  const doLaunch = async () => {
    setError(null)
    if (!prompt.trim()) { setError('Prompt is required'); return }
    let data
    try {
      const res = await generateCategory(requestBody(true))
      data = res.data
      setPreview(data)
    } catch (err) {
      setError(err.response?.data?.detail || err.message)
      return
    }
    const threshold = setup?.cost_confirm_threshold ?? 20
    const skipLine = data.skipped_generated
      ? `\nSkipped ${data.skipped_generated} items that already have a generated image`
      : ''
    const modeLine = data.mode === 'realtime'
      ? `\nMode: Realtime (parallel sync API, ~${maxThreads} threads)`
      : `\nMode: Batch (Vertex batch prediction)`
    const msg = `Launch batch job?\n\n${data.item_count} items in ${data.chunk_count} chunk(s)\nEstimated cost: ~$${data.estimated_cost_usd} ($${data.cost_per_image ?? (mode === 'realtime' ? modelInfo?.sync_cost_per_image ?? 0.039 : modelInfo?.async_cost_per_image ?? 0.0195)}/image)${modeLine}${skipLine}\nQA pause after chunk 1: ${qaGate ? 'ON' : 'OFF'}`
    if (data.estimated_cost_usd >= threshold) {
      const typed = window.prompt(`${msg}\n\nThis exceeds $${threshold}. Type LAUNCH to confirm:`)
      if (typed !== 'LAUNCH') return
    } else if (!window.confirm(msg)) {
      return
    }
    setLaunching(true)
    try {
      const res = await generateCategory(requestBody(false))
      onLaunched(res.data.job_id)
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : err.message)
      if (err.response?.status === 409) {
        window.alert(detail || 'Could not launch the job.')
      }
    } finally {
      setLaunching(false)
    }
  }

  const modelInfo = models.find(m => m.id === model)

  return (
    <div className="space-y-6">
      <SetupBanner setup={setup} onRefresh={() => window.location.reload()} />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-5">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">1. Categories</h3>
          <CategoryPicker
            categories={categories}
            selected={selectedCats}
            onToggle={toggle(setSelectedCats)}
            onSelectAll={() => setSelectedCats(new Set(categories.map(c => c.category)))}
            onClear={() => setSelectedCats(new Set())}
          />
          <p className="mt-1 text-xs text-gray-500">
            {selectedCats.size === 0 ? 'None selected — launch will fail. Pick at least one category.' : `${selectedCats.size} categor${selectedCats.size === 1 ? 'y' : 'ies'} selected`}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">2. Restaurants <span className="text-gray-400 font-normal">(optional filter)</span></h3>
          <RestaurantPicker
            restaurants={restaurants}
            selected={selectedRests}
            onToggle={toggle(setSelectedRests)}
            onSelectAll={() => setSelectedRests(new Set(restaurants.map(r => r.name)))}
            onClear={() => setSelectedRests(new Set())}
          />
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">3. Prompt</h3>
          {templates.length > 0 && (
            <select
              className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              value=""
              onChange={(e) => {
                const t = templates.find(x => String(x.id) === e.target.value)
                if (t) setPrompt(t.prompt)
              }}
            >
              <option value="">Load from Prompt Library...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Describe how to regenerate the food images..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
          />
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeItemDetails}
                onChange={(e) => setIncludeItemDetails(e.target.checked)}
                className="rounded text-orange-500 focus:ring-orange-500"
              />
              Append each item's details (name, description, ingredients, ...) to its prompt
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600">
              <span className="font-medium text-gray-700">Per-item placeholders</span> (filled from each
              item's menu data when the request is built):{' '}
              {['name', 'category', 'restaurant', 'description', 'ingredients', 'extras', 'price'].map(ph => (
                <code key={ph} className="bg-white border border-gray-200 rounded px-1 py-0.5 mr-1 text-orange-700">{`{${ph}}`}</code>
              ))}
              <p className="mt-1">
                Example: <em>Recreate the dish “&#123;name&#125;” for {`{restaurant}`}. Keep it identical; notes: &#123;description&#125;</em>
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">4. Mode</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMode('realtime')}
              className={`p-3 rounded-lg border-2 text-left transition ${
                mode === 'realtime'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className={`h-4 w-4 ${mode === 'realtime' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-800">Realtime</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Fast · parallel sync API · ~$0.039/img
              </p>
            </button>
            <button
              type="button"
              onClick={() => setMode('batch')}
              className={`p-3 rounded-lg border-2 text-left transition ${
                mode === 'batch'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers className={`h-4 w-4 ${mode === 'batch' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-800">Batch</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Slow · Vertex batch · ~$0.0195/img
              </p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
            >
              {models.map(m => (
                <option key={m.id} value={m.id} disabled={!m.available}>
                  {m.name} {m.available ? '' : '(requires access)'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              ~${mode === 'realtime' ? (modelInfo?.sync_cost_per_image || 0.039) : (modelInfo?.async_cost_per_image || 0.0195)}/img ({mode})
            </p>
          </div>
          {mode === 'realtime' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Parallel threads</label>
              <input
                type="number" min="1" max="20" value={maxThreads}
                onChange={(e) => setMaxThreads(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
              <p className="mt-1 text-xs text-gray-500">Concurrent API calls (1-20)</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Chunk size</label>
                <input
                  type="number" min="10" max="1000" value={chunkSize}
                  onChange={(e) => setChunkSize(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Parallel chunks</label>
                <input
                  type="number" min="1" max="8" value={maxInFlight}
                  onChange={(e) => setMaxInFlight(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">Vertex jobs in flight (1-8)</p>
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Aspect ratio</label>
            <select
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
            >
              {['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3'].map(ar => (
                <option key={ar} value={ar}>{ar}</option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={qaGate}
            onChange={(e) => setQaGate(e.target.checked)}
            className="rounded text-orange-500 focus:ring-orange-500"
          />
          <Zap className="h-4 w-4 text-purple-500" />
          Pause after chunk 1 for QA review before spending the rest
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={skipGenerated}
            onChange={(e) => setSkipGenerated(e.target.checked)}
            className="rounded text-orange-500 focus:ring-orange-500"
          />
          Skip items that already have a generated image
        </label>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">5. Reference image <span className="text-gray-400 font-normal">(optional)</span></h3>
          <ReferenceImageUpload dataUrl={refImage} onChange={setRefImage} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={doPreview}
            disabled={previewing || !prompt.trim()}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            {previewing ? 'Previewing...' : 'Preview & cost'}
          </button>
          <button
            onClick={doLaunch}
            disabled={launching || !prompt.trim() || selectedCats.size === 0}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {launching ? 'Launching...' : 'Launch batch job'}
          </button>
        </div>

        {preview && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-1">
            <p className="font-medium">
              {preview.item_count} items · {preview.chunk_count} chunks · ~${preview.estimated_cost_usd}
              {preview.mode === 'realtime' ? ' · realtime' : ' · batch'}
              {refImage ? ' · + reference image on every request' : ''}
            </p>
            {preview.skipped_generated > 0 && (
              <p className="text-amber-700 font-medium">
                {preview.skipped_generated} already-generated items skipped
              </p>
            )}
            <p className="text-blue-700">
              Categories: {Object.entries(preview.categories).map(([k, v]) => `${k} (${v})`).join(', ')}
            </p>
            <p className="text-blue-700">
              Restaurants: {Object.keys(preview.restaurants).length} — {Object.entries(preview.restaurants).slice(0, 12).map(([k, v]) => `${k} (${v})`).join(', ')}
              {Object.keys(preview.restaurants).length > 12 ? ' ...' : ''}
            </p>
            {preview.prompt_example && (
              <div className="mt-2 bg-white border border-blue-100 rounded p-2">
                <p className="text-xs font-medium text-blue-800 mb-1">First item's prompt will be:</p>
                <p className="text-xs text-gray-700 whitespace-pre-wrap">{preview.prompt_example}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function TestPromptTab() {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [templates, setTemplates] = useState([])
  const [models, setModels] = useState([])
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState('gemini-2.5-flash-image')
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [includeItemDetails, setIncludeItemDetails] = useState(false)
  const [refImage, setRefImage] = useState(null)
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    getPromptTemplates().then(r => {
      setTemplates(r.data)
      const def = r.data.find(t => t.is_default) || r.data[0]
      if (def) setPrompt(prev => prev || def.prompt)
    }).catch(() => {})
    getModels().then(r => setModels(r.data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!search.trim()) { setItems([]); return }
    const t = setTimeout(() => {
      searchItems({ search: search.trim(), image_filter: 'with', limit: 12 })
        .then(r => setItems(r.data.items))
        .catch(() => setItems([]))
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  const runTest = async () => {
    setError(null); setResult(null)
    if (!selectedItem) { setError('Pick an item first'); return }
    if (!prompt.trim()) { setError('Prompt is required'); return }
    const cost = testModelInfo?.sync_cost_per_image ?? 0.039
    const ok = window.confirm(
      `Run one test generation?\n\nItem: ${selectedItem.name}\nModel: ${testModelInfo?.name || model}\nEstimated cost: ~$${cost} (sync rate)\n\nNothing is saved; this spends real money.`
    )
    if (!ok) return
    setTesting(true)
    try {
      const res = await testPrompt({
        item_id: selectedItem.id,
        prompt: prompt.trim(),
        model,
        aspect_ratio: aspectRatio,
        include_item_details: includeItemDetails,
        reference_image: refImage || null,
      })
      setResult(res.data)
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : err.message)
    } finally {
      setTesting(false)
    }
  }

  const testModelInfo = models.find(m => m.id === model)
  const testCost = testModelInfo?.sync_cost_per_image ?? 0.039

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-5">
      <p className="text-sm text-gray-600">
        Test your prompt on one item with the exact same request shape as batch jobs —
        [product photo] (+ optional reference image) + per-item prompt. Nothing is saved;
        use the QA gate in a small batch when you're happy.
      </p>

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">1. Item</h3>
        <div className="relative mb-2">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items (name, restaurant, category)..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
          />
        </div>
        {items.length > 0 && !selectedItem && (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {items.map(it => (
              <button
                key={it.id}
                onClick={() => { setSelectedItem(it); setSearch('') }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left"
              >
                {it.image_path && (
                  <AuthedImage src={photoUrl(it.image_path)} alt="" className="h-10 w-10 rounded object-cover" />
                )}
                <span className="flex-1 text-sm text-gray-800">{it.name}</span>
                <span className="text-xs text-gray-500">{it.restaurant_name} · {it.category}</span>
              </button>
            ))}
          </div>
        )}
        {selectedItem && (
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            {selectedItem.image_path && (
              <AuthedImage src={photoUrl(selectedItem.image_path)} alt="" className="h-14 w-14 rounded object-cover" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{selectedItem.name}</p>
              <p className="text-xs text-gray-500">{selectedItem.restaurant_name} · {selectedItem.category}</p>
            </div>
            <button onClick={() => setSelectedItem(null)} className="text-xs text-red-600 hover:underline">
              Change
            </button>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">2. Prompt</h3>
        {templates.length > 0 && (
          <select
            className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
            value=""
            onChange={(e) => {
              const t = templates.find(x => String(x.id) === e.target.value)
              if (t) setPrompt(t.prompt)
            }}
          >
            <option value="">Load from Prompt Library...</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Same prompt you plan to use for the batch..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
        />
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={includeItemDetails}
            onChange={(e) => setIncludeItemDetails(e.target.checked)}
            className="rounded text-orange-500 focus:ring-orange-500"
          />
          Append this item's details (name, description, ingredients, ...) to the prompt
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
          >
            {models.map(m => (
              <option key={m.id} value={m.id} disabled={!m.available}>
                {m.name} {m.available ? '' : '(requires access)'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Aspect ratio</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
          >
            {['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3'].map(ar => (
              <option key={ar} value={ar}>{ar}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">3. Reference image <span className="text-gray-400 font-normal">(optional)</span></h3>
        <ReferenceImageUpload dataUrl={refImage} onChange={setRefImage} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={runTest}
          disabled={testing || !selectedItem || !prompt.trim()}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          <Beaker className="h-4 w-4" />
          {testing ? 'Generating (a few seconds)...' : 'Run test'}
        </button>
        <p className="text-xs text-gray-500">
          Estimated cost: <span className="font-semibold text-gray-700">~${testCost}</span> per test (sync)
          {testModelInfo ? ` · ${testModelInfo.name}` : ''}
        </p>
      </div>

      {result && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-green-800 text-sm font-medium">
            <CheckCircle2 className="h-4 w-4" />
            Test succeeded — ~${result.cost_usd} in {result.generation_time_sec}s
            {result.reference_image_used ? ' · reference image included' : ''}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Original</p>
              {result.item?.image_path && (
                <AuthedImage src={photoUrl(result.item.image_path)} alt="original" className="w-full rounded-lg border border-gray-200" />
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Generated (not saved)</p>
              <img src={result.image_data_url} alt="generated" className="w-full rounded-lg border border-green-200" />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Prompt actually sent:</p>
            <p className="text-xs text-gray-700 bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap">
              {result.formatted_prompt}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function JobItemsPanel({ jobId, counts, showFailedOnly }) {
  const [items, setItems] = useState([])
  useEffect(() => {
    let alive = true
    const load = () => getJobItems(jobId, showFailedOnly ? { status: 'failed' } : {})
      .then(r => { if (alive) setItems(r.data.items) }).catch(() => {})
    load()
    const t = setInterval(load, 5000)
    return () => { alive = false; clearInterval(t) }
  }, [jobId, showFailedOnly])

  const failed = items.filter(i => i.job_item_status === 'failed')
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
        <span className="bg-gray-100 rounded px-2 py-0.5">{counts.done} done</span>
        <span className="bg-red-100 text-red-700 rounded px-2 py-0.5">{counts.failed} failed</span>
        <span className="bg-yellow-100 text-yellow-800 rounded px-2 py-0.5">{counts.pending} pending</span>
        <span className="bg-blue-100 text-blue-700 rounded px-2 py-0.5">{counts.running} running</span>
      </div>
      <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {(showFailedOnly ? failed : items).slice(0, 300).map((it) => (
          <div key={it.job_item_id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
            <StatusChip status={it.job_item_status} />
            <span className="flex-1 truncate">{it.name}</span>
            <span className="text-xs text-gray-500 truncate max-w-[160px]">{it.restaurant_name}</span>
            {it.error && <span className="text-xs text-red-600 truncate max-w-[220px]" title={it.error}>{it.error}</span>}
          </div>
        ))}
        {items.length === 0 && <div className="p-3 text-sm text-gray-500 text-center">No items</div>}
      </div>
      {failed.length > 300 && <p className="text-xs text-gray-500">Showing first 300 failures</p>}
    </div>
  )
}

function JobResultsPanel({ jobId, counts }) {
  const [items, setItems] = useState([])
  useEffect(() => {
    let alive = true
    getJobItems(jobId, { status: 'done' })
      .then(r => { if (alive) setItems(r.data.items) }).catch(() => {})
    return () => { alive = false }
  }, [jobId])

  if (items.length === 0) {
    return <div className="p-4 text-sm text-gray-500 text-center">No completed items yet</div>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span className="bg-green-100 text-green-700 rounded px-2 py-0.5">{items.length} completed — click image to open full size</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
        {items.map(it => (
          <div key={it.job_item_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="grid grid-cols-2 divide-x divide-gray-100">
              <AuthedImage src={photoUrl(it.image_path)} alt={it.name} className="w-full h-28 object-cover" fallback="—" />
              {it.output_filename && (
                <AuthedImage src={mediaGenFileUrl(it.restaurant_name, it.output_filename)} alt={`${it.name} generated`} className="w-full h-28 object-cover" fallback="—" />
              )}
            </div>
            <div className="px-2 py-1.5 text-xs text-gray-700 truncate" title={it.name}>
              {it.name} <span className="text-gray-400">({it.restaurant_name})</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function JobEventsPanel({ jobId }) {
  const [events, setEvents] = useState([])
  useEffect(() => {
    let alive = true
    const load = () => getJobEvents(jobId).then(r => { if (alive) setEvents(r.data.events) }).catch(() => {})
    load()
    const t = setInterval(load, 8000)
    return () => { alive = false; clearInterval(t) }
  }, [jobId])
  return (
    <div className="max-h-56 overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 p-2 font-mono text-xs space-y-0.5">
      {events.map(ev => (
        <div key={ev.id} className={ev.level === 'error' ? 'text-red-600' : ev.level === 'warn' ? 'text-amber-700' : 'text-gray-700'}>
          <span className="text-gray-400">{fmtLocal(ev.created_at)}</span> {ev.message}
        </div>
      ))}
      {events.length === 0 && <div className="text-gray-500">No events yet</div>}
    </div>
  )
}

function QaReviewPanel({ job, onDecision }) {
  const [samples, setSamples] = useState([])
  const [busy, setBusy] = useState(false)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    getJobItems(job.id, { status: 'done' })
      .then(r => setSamples(r.data.items.filter(i => i.chunk_index === 0)))
      .catch(() => {})
  }, [job.id])

  const decide = async (approve) => {
    setBusy(true)
    try {
      await reviewJob(job.id, approve)
      onDecision()
    } finally {
      setBusy(false)
    }
  }

  const shown = showAll ? samples : samples.slice(0, 12)

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 text-purple-900 font-medium text-sm">
        <Eye className="h-4 w-4" />
        QA review — inspect chunk 1 samples before the rest of the budget is spent
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {shown.map(s => (
          <div key={s.job_item_id} className="bg-white rounded-lg border border-purple-100 overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-gray-100">
              <AuthedImage src={photoUrl(s.image_path)} alt={s.name} className="w-full h-24 object-cover" fallback="—" />
              {s.output_filename && (
                <AuthedImage
                  src={mediaGenFileUrl(s.restaurant_name, s.output_filename)}
                  alt={`${s.name} generated`}
                  className="w-full h-24 object-cover"
                  fallback="—"
                />
              )}
            </div>
            <div className="px-2 py-1 text-xs text-gray-700 truncate" title={s.name}>{s.name}</div>
          </div>
        ))}
      </div>
      {samples.length > 12 && (
        <button onClick={() => setShowAll(!showAll)} className="text-xs text-purple-700 hover:underline">
          {showAll ? 'Show fewer' : `Show all ${samples.length} samples`}
        </button>
      )}
      <div className="flex gap-3">
        <button
          onClick={() => decide(true)}
          disabled={busy}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          <CheckCircle2 className="h-4 w-4" /> Approve & continue
        </button>
        <button
          onClick={() => decide(false)}
          disabled={busy}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          <XCircle className="h-4 w-4" /> Reject & cancel
        </button>
      </div>
    </div>
  )
}

function JobPromptBlock({ job }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 rounded px-1.5 py-0.5 font-mono">
          {job.model}
        </span>
        <button
          onClick={() => navigator.clipboard?.writeText(job.prompt || '')}
          className="text-[11px] text-gray-500 hover:text-gray-800 flex items-center gap-1"
          title="Copy prompt"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
      <p className={`text-xs text-gray-600 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}>
        {job.prompt}
      </p>
      {(job.prompt || '').length > 160 && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-orange-600 hover:underline">
          {expanded ? 'Show less' : 'Show full prompt'}
        </button>
      )}
    </div>
  )
}

function JobCard({ job, expanded, onToggle, onChanged, focusItems }) {
  const [detail, setDetail] = useState(null)
  const [showFailedOnly, setShowFailedOnly] = useState(false)
  const [viewMode, setViewMode] = useState('items') // 'items' | 'results'
  const [busy, setBusy] = useState(false)
  const active = ACTIVE_STATUSES.has(job.status)
  const showResults = ['completed', 'partial'].includes(job.status) && detail?.counts?.done > 0

  const loadDetail = useCallback(() => {
    getJobItems(job.id).then(r => setDetail(r.data)).catch(() => {})
  }, [job.id])

  useEffect(() => {
    if (!expanded) return
    loadDetail()
    if (!active) return
    const t = setInterval(loadDetail, 5000)
    return () => clearInterval(t)
  }, [expanded, active, loadDetail])

  useEffect(() => { if (expanded && focusItems) setShowFailedOnly(true) }, [expanded, focusItems])

  const total = job.total_items || (detail ? detail.counts.done + detail.counts.failed + detail.counts.pending + detail.counts.running : 0)
  const done = detail?.counts.done ?? job.completed_items ?? 0
  const failed = detail?.counts.failed ?? job.failed_items ?? 0
  const pct = total ? Math.round((done / total) * 100) : 0
  const rate = active
    ? (detail?.images_per_min ?? job.images_per_min ?? 0)
    : 0

  const act = async (fn) => {
    setBusy(true)
    try { await fn(); onChanged() } catch (err) {
      window.alert(err.response?.data?.detail || err.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 rounded-t-xl"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <span className="text-sm font-semibold text-gray-900">Job #{job.id}</span>
        <StatusChip status={job.status} />
        <span className="text-xs text-gray-500">{job.job_type}</span>
        <div className="flex-1 min-w-[120px] max-w-xs mx-2">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${job.status === 'failed' || job.status === 'cancelled' ? 'bg-gray-400' : pct === 100 ? 'bg-green-500' : 'bg-orange-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <span className="text-xs text-gray-600 w-40 text-right">
          {done}/{total || '?'} · {failed} failed
          {active && rate > 0 && (
            <span className="ml-1 text-orange-600 font-medium">· {rate.toFixed(1)}/min</span>
          )}
        </span>
        <span className="text-xs text-gray-400 hidden md:inline">{fmtLocal(job.created_at)}</span>
      </button>

      <div className="px-4 pb-4 space-y-3">
        {expanded && (
          <>
            <JobPromptBlock job={job} />
            {job.status === 'awaiting_review' && detail && (
              <QaReviewPanel job={job} onDecision={onChanged} />
            )}
            {detail && (
              <>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setViewMode('items')}
                    className={`text-xs px-2 py-1 rounded border ${viewMode === 'items' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    Items
                  </button>
                  {showResults && (
                    <button
                      onClick={() => setViewMode('results')}
                      className={`text-xs px-2 py-1 rounded border ${viewMode === 'results' ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    >
                      Results
                    </button>
                  )}
                  {viewMode === 'items' && (
                    <button
                      onClick={() => setShowFailedOnly(!showFailedOnly)}
                      className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1 w-fit"
                    >
                      {showFailedOnly ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {showFailedOnly ? 'Showing failures only' : 'Show all items'}
                    </button>
                  )}
                </div>
                {viewMode === 'items' && (
                  <JobItemsPanel jobId={job.id} counts={detail.counts} showFailedOnly={showFailedOnly} />
                )}
                {viewMode === 'results' && showResults && (
                  <JobResultsPanel jobId={job.id} counts={detail.counts} />
                )}
              </>
            )}
            <JobEventsPanel jobId={job.id} />
            <div className="flex flex-wrap gap-2">
              {['queued', 'running', 'awaiting_review'].includes(job.status) && (
                <button
                  onClick={() => act(() => pauseJob(job.id))}
                  disabled={busy || job.status === 'pausing'}
                  className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 disabled:opacity-50 flex items-center gap-1"
                >
                  <Clock className="h-3.5 w-3.5" /> {job.status === 'pausing' ? 'Pausing...' : 'Pause'}
                </button>
              )}
              {['queued', 'running', 'awaiting_review'].includes(job.status) && (
                <button
                  onClick={() => act(() => cancelJob(job.id))}
                  disabled={busy || job.status === 'cancelling'}
                  className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
                >
                  <XCircle className="h-3.5 w-3.5" /> Cancel
                </button>
              )}
              {['failed', 'partial', 'cancelled', 'paused'].includes(job.status) && (
                <button
                  onClick={() => act(() => resumeJob(job.id))}
                  disabled={busy}
                  className="px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-xs font-medium hover:bg-green-50 disabled:opacity-50 flex items-center gap-1"
                >
                  <PlayCircle className="h-3.5 w-3.5" /> {job.status === 'paused' ? 'Resume' : 'Resume unfinished'}
                </button>
              )}
              {['failed', 'partial', 'cancelled', 'paused'].includes(job.status) && failed > 0 && (
                <button
                  onClick={() => act(() => retryFailedJob(job.id))}
                  disabled={busy}
                  title="Retry only the failed items of THIS job, in place (reuses Google's finished work; skips items another job already completed)"
                  className="px-3 py-1.5 border border-amber-300 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-50 disabled:opacity-50 flex items-center gap-1"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Retry failed ({failed})
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function JobsTab({ highlightJobId }) {
  const [jobs, setJobs] = useState([])
  const [expandedId, setExpandedId] = useState(highlightJobId || null)
  const [loading, setLoading] = useState(true)
  const highlightRef = useRef(highlightJobId)

  const load = useCallback(() => {
    getJobs({ limit: 50 })
      .then(r => setJobs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 10000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    if (highlightJobId && highlightJobId !== highlightRef.current) {
      highlightRef.current = highlightJobId
      setExpandedId(highlightJobId)
    }
  }, [highlightJobId])

  const anyActive = jobs.some(j => ACTIVE_STATUSES.has(j.status))

  if (loading && jobs.length === 0) return <div className="text-sm text-gray-500 p-4">Loading jobs...</div>
  if (jobs.length === 0) return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
      No batch jobs yet. Launch one from the "New batch" tab.
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <RefreshCw className={`h-3.5 w-3.5 ${anyActive ? 'animate-spin' : ''}`} />
        {anyActive ? 'Auto-refreshing every 10s (jobs active)' : 'Refreshes every 10s'}
        <button onClick={load} className="ml-auto underline hover:text-gray-700">Refresh now</button>
      </div>
      {jobs.map(job => (
        <JobCard
          key={job.id}
          job={job}
          expanded={expandedId === job.id}
          focusItems={highlightJobId === job.id}
          onToggle={() => setExpandedId(expandedId === job.id ? null : job.id)}
          onChanged={load}
        />
      ))}
    </div>
  )
}

function GenerationThumb({ gen, size, restaurantName, onToggleFlag }) {
  const title = [
    `v${gen.version} — ${fmtLocal(gen.created_at)}`,
    `model: ${gen.model}`,
    gen.aspect_ratio ? `aspect: ${gen.aspect_ratio}` : null,
    `cost: $${(gen.cost_usd ?? 0).toFixed(4)}`,
    gen.job_id ? `job: #${gen.job_id}` : null,
    gen.flagged ? 'FLAGGED' : null,
    `prompt: ${(gen.prompt || '').slice(0, 300)}${(gen.prompt || '').length > 300 ? '…' : ''}`,
  ].filter(Boolean).join('\n')
  return (
    <div className="shrink-0" title={title}>
      <div className="relative">
        <AuthedImage
          src={mediaGenFileUrl(restaurantName, gen.output_filename)}
          alt={`v${gen.version}`}
          style={{ width: size, height: size }}
          className={`object-cover bg-gray-100 rounded border ${gen.flagged ? 'border-amber-400 ring-2 ring-amber-300' : 'border-gray-200'}`}
          fallback="—"
        />
        {onToggleFlag && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onToggleFlag(gen) }}
            title={gen.flagged ? 'Unflag this result' : 'Flag this result'}
            className={`absolute top-1 right-1 rounded-full p-1 shadow-sm ${
              gen.flagged
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-white/90 text-gray-500 hover:text-amber-600 hover:bg-white'
            }`}
          >
            <Flag className={`h-3.5 w-3.5 ${gen.flagged ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
        <span className={`font-semibold ${gen.approved ? 'text-green-700' : 'text-gray-600'}`}>v{gen.version}</span>
        <span>{fmtLocalShort(gen.created_at)}</span>
        {gen.approved && <CheckCircle2 className="h-3 w-3 text-green-600" />}
        {gen.flagged && <Flag className="h-3 w-3 text-amber-600 fill-current" />}
      </div>
    </div>
  )
}

function ResultsTab() {
  const [jobs, setJobs] = useState([])
  const [jobId, setJobId] = useState(null)
  const [items, setItems] = useState([])
  const [size, setSize] = useState(220)
  const [renderLimit, setRenderLimit] = useState(120)
  const [loading, setLoading] = useState(false)
  const [latestOnly, setLatestOnly] = useState(false)
  const [showFailed, setShowFailed] = useState(false)
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [flagged, setFlagged] = useState([])
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    getJobs({ limit: 50 }).then(r => {
      const withResults = r.data.filter(j =>
        ['completed', 'partial', 'awaiting_review'].includes(j.status) || (j.completed_items || 0) > 0)
      setJobs(withResults)
      setJobId(prev => prev && withResults.some(j => j.id === prev) ? prev
        : (withResults[0]?.id ?? null))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!jobId) { setItems([]); return }
    setLoading(true)
    getJobResults(jobId)
      .then(r => setItems(r.data.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [jobId, reloadKey])

  useEffect(() => {
    if (!flaggedOnly) return
    setLoading(true)
    getFlaggedResults()
      .then(r => setFlagged(r.data.generations))
      .catch(() => setFlagged([]))
      .finally(() => setLoading(false))
  }, [flaggedOnly, reloadKey])

  const handleFlag = async (gen) => {
    const next = !gen.flagged
    try {
      await toggleFlag(gen.id, next)
      if (next) {
        setItems(prev => prev.map(it => ({
          ...it,
          generations: it.generations.map(g => g.id === gen.id ? { ...g, flagged: true } : g),
        })))
        setFlagged(prev => prev.some(g => g.id === gen.id) ? prev : [{ ...gen, flagged: true }, ...prev])
      } else {
        setItems(prev => prev.map(it => ({
          ...it,
          generations: it.generations.map(g => g.id === gen.id ? { ...g, flagged: false } : g),
        })))
        setFlagged(prev => prev.filter(g => g.id !== gen.id))
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('flag failed', e)
    }
  }

  // When the "Flagged only" filter is on, group all flagged generations
  // (across every job) by item so they render with the same card layout.
  const flaggedItems = flaggedOnly ? (() => {
    const byItem = new Map()
    for (const g of flagged) {
      if (!byItem.has(g.item_id)) {
        byItem.set(g.item_id, {
          job_item_id: `flag-${g.item_id}`,
          item_id: g.item_id,
          name: g.name,
          restaurant_name: g.restaurant_name,
          image_path: g.image_path,
          job_item_status: 'flagged',
          generations: [],
        })
      }
      byItem.get(g.item_id).generations.push({
        id: g.id, version: g.version, model: g.model, prompt: g.prompt,
        output_filename: g.output_filename, output_path: g.output_path,
        status: g.status, approved: g.approved, flagged: g.flagged,
        cost_usd: g.cost_usd, generation_time_sec: g.generation_time_sec,
        created_at: g.created_at, aspect_ratio: g.aspect_ratio, job_id: g.job_id,
      })
    }
    return [...byItem.values()]
  })() : []

  const job = jobs.find(j => j.id === jobId)
  const source = flaggedOnly ? flaggedItems : items
  const visible = source.filter(it =>
    (flaggedOnly || showFailed || it.generations.length > 0))
  const shown = visible.slice(0, renderLimit)

  if (jobs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        No completed batch jobs yet — launch one from "New batch", then come back here to compare results.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Job
            <select
              value={jobId ?? ''}
              disabled={flaggedOnly}
              onChange={(e) => { setJobId(Number(e.target.value)); setRenderLimit(120) }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  #{j.id} — {j.completed_items ?? '?'} images — {j.model} — {j.status}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 flex-1 min-w-[240px]">
            Image size
            <input
              type="range" min="80" max="420" step="10"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="flex-1 accent-orange-500"
            />
            <span className="text-xs text-gray-500 w-12 text-right">{size}px</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={latestOnly} onChange={(e) => setLatestOnly(e.target.checked)}
              className="accent-orange-500" />
            Latest version only
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={showFailed} onChange={(e) => setShowFailed(e.target.checked)}
              className="accent-orange-500" />
            Include failed
          </label>
          <label className={`flex items-center gap-1.5 text-sm cursor-pointer rounded-lg px-2 py-1 border ${flaggedOnly ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-transparent text-gray-700'}`}>
            <input type="checkbox" checked={flaggedOnly} onChange={(e) => { setFlaggedOnly(e.target.checked); setRenderLimit(120) }}
              className="accent-amber-500" />
            <Flag className={`h-3.5 w-3.5 ${flaggedOnly ? 'text-amber-600 fill-current' : 'text-gray-400'}`} />
            Flagged only
          </label>
          <button
            onClick={() => { setRenderLimit(120); setReloadKey(k => k + 1) }}
            className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        {!flaggedOnly && job && <JobPromptBlock job={job} />}
        <p className="text-xs text-gray-500">
          {loading ? 'Loading...'
            : flaggedOnly
              ? `${flagged.length} flagged generation${flagged.length === 1 ? '' : 's'} across all jobs — click the flag on any result to unflag it`
              : `${visible.length} images, ${visible.reduce((n, it) => n + it.generations.length, 0)} generations — every attempt kept; click the flag to mark a result, hover a thumbnail for full generation info, click to open full size`}
        </p>
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${size * 2 + 12}px, 1fr))` }}
      >
        {shown.map(it => {
          const gens = latestOnly && it.generations.length
            ? [it.generations[it.generations.length - 1]]
            : it.generations
          return (
            <div key={it.job_item_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="px-2 py-1.5 text-xs text-gray-700 truncate border-b border-gray-100" title={`${it.name} — ${it.restaurant_name}`}>
                {it.name} <span className="text-gray-400">({it.restaurant_name})</span>
                {it.generations.length > 1 && (
                  <span className="ml-1 text-orange-700 font-semibold">×{it.generations.length}</span>
                )}
              </div>
              <div className="p-2 flex flex-wrap gap-2 items-start">
                <div className="shrink-0" title="Original photo">
                  <AuthedImage
                    src={photoUrl(it.image_path)} alt={it.name}
                    style={{ width: size, height: size }}
                    className="object-cover bg-gray-100 rounded border border-gray-200"
                    fallback="—"
                  />
                  <div className="text-[10px] text-gray-400 mt-0.5">original</div>
                </div>
                {gens.map(g => (
                  <GenerationThumb key={g.id} gen={g} size={size} restaurantName={it.restaurant_name} onToggleFlag={handleFlag} />
                ))}
                {gens.length === 0 && (
                  <div className="text-xs text-red-600 self-center">
                    {it.job_item_status === 'failed' ? `failed: ${it.job_item_error || 'unknown error'}` : it.job_item_status}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {visible.length > renderLimit && (
        <button
          onClick={() => setRenderLimit(l => l + 240)}
          className="mx-auto block px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          Show more ({renderLimit} of {visible.length} shown)
        </button>
      )}
    </div>
  )
}

export function BatchJobs() {
  const [tab, setTab] = useState('jobs')
  const [setup, setSetup] = useState(null)
  const [highlightJobId, setHighlightJobId] = useState(null)

  const loadSetup = useCallback(() => {
    getSetupStatus().then(r => setSetup(r.data)).catch(() => setSetup(null))
  }, [])

  useEffect(() => {
    loadSetup()
    const t = setInterval(loadSetup, 30000)
    return () => clearInterval(t)
  }, [loadSetup])

  const ready = setup?.ready

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="h-6 w-6 text-orange-500" />
            Batch Regeneration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Category-wide image regeneration via Vertex AI batch jobs (~50% cheaper than sync)
            {setup?.telegram ? ' · Telegram notifications on' : ''}
          </p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setTab('jobs')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === 'jobs' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
          >
            Jobs
          </button>
          <button
            onClick={() => setTab('new')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === 'new' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
          >
            New batch
          </button>
          <button
            onClick={() => setTab('test')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${tab === 'test' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
          >
            <Beaker className="h-3.5 w-3.5" />
            Test prompt
          </button>
          <button
            onClick={() => setTab('results')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${tab === 'results' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
          >
            <Images className="h-3.5 w-3.5" />
            Results
          </button>
        </div>
      </div>

      {!ready && setup && tab === 'jobs' && <SetupBanner setup={setup} onRefresh={loadSetup} />}

      {tab === 'new' && <NewBatchTab setup={setup} onLaunched={(jobId) => { setHighlightJobId(jobId); setTab('jobs'); loadSetup() }} />}
      {tab === 'test' && <TestPromptTab />}
      {tab === 'jobs' && <JobsTab highlightJobId={highlightJobId} />}
      {tab === 'results' && <ResultsTab />}
    </div>
  )
}
